import type { TFunction } from "i18next";

type MetadataLabel = {
    label?: string | null;
    name?: string | null;
};

export const getMetadataLabelKey = (metadata: MetadataLabel): string => {
    const label = metadata.label?.trim();
    if (label) {
        return label;
    }

    return metadata.name?.trim() || "";
};

export const translateMetadataLabel = (
    t: TFunction,
    metadata: MetadataLabel
): string => {
    const key = getMetadataLabelKey(metadata);
    return key ? t(key) : "";
};

// Case-insensitive to match TranslatableInput's own `isTranslationKey` (the component that
// actually creates these keys) -- a dynamic Fluss key like `fluss_manual_task.Activity_1m1rz87.
// description` embeds a raw bpmn-js element id, which always has an uppercase type prefix
// (Activity_/Gateway_/Event_/...). A case-sensitive pattern here silently failed to recognize
// those as keys, so `t()` was never called and the raw key showed up verbatim on the inbox task
// screen instead of the author's saved instructions -- the translation existed in sys_translations
// the whole time; this function alone was refusing to look it up.
const TRANSLATION_KEY_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z0-9_-]+)+$/i;
export const isTranslationKey = (value: string): boolean => TRANSLATION_KEY_PATTERN.test(value);

/**
 * Resolves a record value that may itself be a translation key (e.g. a REFERENCE
 * display value or a TEXT value storing `task_mgmt.table.sla_definition`) into the
 * translated string. Only strings shaped like a namespaced key are looked up; if `t`
 * doesn't find a match it returns the original value (i18next falls back to the key
 * itself, so `translated === value` is the "not found" signal). Anything else
 * (non-string, null, undefined, literal text) passes through unchanged.
 */
export const resolveDisplayText = <T>(t: TFunction, value: T, options?: Record<string, unknown>): T | string => {
    if (typeof value !== "string" || !TRANSLATION_KEY_PATTERN.test(value)) {
        return value;
    }

    const translated = options ? t(value, options) : t(value);
    return typeof translated === "string" && translated !== value ? translated : value;
};

const EMBEDDED_KEY_PATTERN = /record\.[a-z0-9_]+\.label\.[a-z0-9_:-]+/gi;

/**
 * Resolve chaves de tradução embutidas num texto composto, como o nome de uma view estendida
 * ("<chave da view> (<chave da tabela>)"). Chaves sem tradução e texto comum ficam como estão.
 */
export const resolveEmbeddedKeys = (t: TFunction, value: string, options?: Record<string, unknown>): string =>
    value.replace(EMBEDDED_KEY_PATTERN, key => {
        const translated = options ? t(key, options) : t(key);
        return typeof translated === "string" && translated ? translated : key;
    });
